import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { safeHotelImageUrl } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey, useCatalogAdminCapability } from './queries';
import { EditorialSnapshot, Publication, inheritedField, moveImage, previewKey, restoreField } from './editorial';
interface Preview {
  hotel_id: string;
  version: number;
  published: true;
  content: EditorialSnapshot;
  provenance: Record<string, string>;
}
interface History {
  data: Array<{
    event_id: string;
    version: number;
    operation: string;
    actor_id: string;
    reason: string;
    created_at: string;
  }>;
  meta: { has_more: boolean };
}
export function CatalogEditorialPage() {
  const { hotelId } = useParams();
  return <CatalogEditorialEditor key={hotelId} hotelId={hotelId ?? ''} />;
}
export function CatalogEditorialEditor({ hotelId }: { hotelId: string }) {
  const { t } = useTranslation();
  const capability = useCatalogAdminCapability();
  const cache = useQueryClient();
  const path = `hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}`;
  const key = [...catalogAdminKey, 'editorial', hotelId];
  const publication = useQuery(
    [...key, 'publication'],
    async () => (await client.get<{ data: Publication }>(`${path}/publication`)).data.data,
    { enabled: capability.data?.allowed === true && !!hotelId, retry: false },
  );
  const [offset, setOffset] = useState(0);
  const history = useQuery(
    [...key, 'history', offset],
    async () => (await client.get<History>(`${path}/history`, { params: { offset } })).data,
    { enabled: !!publication.data, retry: false },
  );
  const imageCapability = useQuery(
    [...catalogAdminKey, 'images'],
    async () =>
      (
        await client.get<{ enabled: boolean; max_bytes: number; mime_types: string[] }>(
          'hotel-catalog-admin/images/capability',
        )
      ).data,
    { enabled: capability.data?.allowed === true, retry: false },
  );
  const [draft, setDraft] = useState<EditorialSnapshot | null>(null);
  const [version, setVersion] = useState(0);
  const [reason, setReason] = useState('');
  const [language, setLanguage] = useState('es');
  const [conflict, setConflict] = useState(false);
  const [previewed, setPreviewed] = useState<{ key: string; data: Preview } | null>(null);
  const currentKey = previewKey(version, draft ?? {}, language);
  const preview = useMutation(
    async (input: { key: string; overrides: EditorialSnapshot; version: number; language: string }) => ({
      key: input.key,
      data: (
        await client.post<{ data: Preview }>(`${path}/preview`, {
          expected_version: input.version,
          overrides: input.overrides,
          language: input.language,
        })
      ).data.data,
    }),
    {
      onSuccess: setPreviewed,
      onError: (error: any) => {
        if (error?.response?.status === 409) setConflict(true);
      },
    },
  );
  const save = useMutation(
    async () => {
      if (!draft || !reason.trim() || previewed?.key !== currentKey || conflict) throw new Error('Preview required');
      await client.patch(`${path}/content`, { expected_version: version, overrides: draft, reason: reason.trim() });
    },
    {
      onSuccess: () => {
        setDraft(null);
        setPreviewed(null);
        cache.invalidateQueries(key);
      },
      onError: (error: any) => {
        if (error?.response?.status === 409) setConflict(true);
      },
    },
  );
  const upload = useMutation(
    async (file: File) => {
      const config = imageCapability.data;
      if (!config?.enabled || file.size > config.max_bytes || !config.mime_types.includes(file.type))
        throw new Error('Invalid image');
      const form = new FormData();
      form.append('file', file);
      const response = (await client.post<{ data: { url: string } }>('hotel-catalog-admin/images', form)).data.data;
      if (!safeHotelImageUrl(response.url)) throw new Error('Invalid image URL');
      return response.url;
    },
    {
      onSuccess: (url) => {
        setDraft((current) =>
          current
            ? {
                ...current,
                images: [...(inheritedField(publication.data?.snapshot ?? {}, current, 'images') ?? []), { url }],
              }
            : current,
        );
        setPreviewed(null);
      },
    },
  );
  if (capability.isLoading) return <Typography>{t('hotelEditorial.loading')}</Typography>;
  if (!capability.data?.allowed) return <Alert severity="info">{t('hotelEditorial.unavailable')}</Alert>;
  if (publication.isLoading) return <Typography>{t('hotelEditorial.loading')}</Typography>;
  if (publication.isError || !publication.data)
    return (
      <Alert severity="warning">
        {t('hotelEditorial.noPublication')}{' '}
        <Button onClick={() => publication.refetch()}>{t('hotelEditorial.reload')}</Button>
      </Alert>
    );
  const stored = publication.data;
  const displayed = draft ?? stored.overrides;
  const descriptions = inheritedField(stored.snapshot, displayed, 'descriptions') ?? [];
  const images = inheritedField(stored.snapshot, displayed, 'images') ?? [];
  const locked = conflict || save.isLoading;
  const change = (next: EditorialSnapshot) => {
    setDraft(next);
    setPreviewed(null);
  };
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4">{t('hotelEditorial.title')}</Typography>
      <Typography>{inheritedField(stored.snapshot, displayed, 'display_name') ?? hotelId}</Typography>
      <Typography variant="caption">
        {hotelId} · {t('hotelEditorial.version', { version: stored.version })}
      </Typography>
      <Alert severity="info" sx={{ my: 2 }}>
        {t('hotelEditorial.global')}
      </Alert>
      {!stored.published ? (
        <Alert severity="warning">{t('hotelEditorial.withdrawn')}</Alert>
      ) : !draft ? (
        <Button
          onClick={() => {
            setDraft(JSON.parse(JSON.stringify(stored.overrides)));
            setVersion(stored.version);
            setReason('');
            setConflict(false);
            setPreviewed(null);
            preview.reset();
            save.reset();
          }}
        >
          {t('hotelEditorial.edit')}
        </Button>
      ) : (
        <Stack spacing={2}>
          <TextField
            label={t('hotelEditorial.name')}
            value={inheritedField(stored.snapshot, draft, 'display_name') ?? ''}
            disabled={locked}
            onChange={(e) => change({ ...draft, display_name: e.target.value })}
            inputProps={{ maxLength: 512 }}
          />
          <Button disabled={locked} onClick={() => change(restoreField(draft, 'display_name'))}>
            {t('hotelEditorial.restoreName')}
          </Button>
          <Typography variant="h6">{t('hotelEditorial.descriptions')}</Typography>
          {!Object.prototype.hasOwnProperty.call(draft, 'descriptions') && (
            <Button
              disabled={locked}
              onClick={() => change({ ...draft, descriptions: JSON.parse(JSON.stringify(descriptions)) })}
            >
              {t('hotelEditorial.customizeText')}
            </Button>
          )}
          {descriptions.map((item, index) => (
            <Stack key={index} spacing={1}>
              <Stack direction="row" spacing={1}>
                <TextField
                  label={t('hotelEditorial.language')}
                  value={item.language}
                  disabled={locked || !('descriptions' in draft)}
                  onChange={(e) =>
                    change({
                      ...draft,
                      descriptions: descriptions.map((row, i) =>
                        i === index ? { ...row, language: e.target.value } : row,
                      ),
                    })
                  }
                  inputProps={{ maxLength: 16 }}
                />
                <TextField
                  label={t('hotelEditorial.kind')}
                  value={item.kind}
                  disabled={locked || !('descriptions' in draft)}
                  onChange={(e) =>
                    change({
                      ...draft,
                      descriptions: descriptions.map((row, i) =>
                        i === index ? { ...row, kind: e.target.value } : row,
                      ),
                    })
                  }
                  inputProps={{ maxLength: 128 }}
                />
              </Stack>
              <TextField
                multiline
                minRows={3}
                label={t('hotelEditorial.text')}
                value={item.text}
                disabled={locked || !('descriptions' in draft)}
                onChange={(e) =>
                  change({
                    ...draft,
                    descriptions: descriptions.map((row, i) => (i === index ? { ...row, text: e.target.value } : row)),
                  })
                }
                inputProps={{ maxLength: 20000 }}
              />
              <Button
                disabled={locked || !('descriptions' in draft)}
                onClick={() => change({ ...draft, descriptions: descriptions.filter((_, i) => i !== index) })}
              >
                {t('hotelEditorial.remove')}
              </Button>
            </Stack>
          ))}
          <Button
            disabled={locked || descriptions.length >= 40}
            onClick={() =>
              change({ ...draft, descriptions: [...descriptions, { language, kind: 'GENERAL', text: '' }] })
            }
          >
            {t('hotelEditorial.addText')}
          </Button>
          <Button disabled={locked} onClick={() => change(restoreField(draft, 'descriptions'))}>
            {t('hotelEditorial.restoreText')}
          </Button>
          <Typography variant="h6">{t('hotelEditorial.gallery')}</Typography>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            {images.map((image, index) => (
              <Box key={`${index}-${image.url}`}>
                {safeHotelImageUrl(image.url) && (
                  <img
                    src={image.url}
                    alt={t('hotelEditorial.image', { index: index + 1 })}
                    width={160}
                    height={110}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    style={{ objectFit: 'cover' }}
                  />
                )}
                <Stack direction="row">
                  <Button
                    disabled={locked || index === 0}
                    onClick={() => change({ ...draft, images: moveImage(images, index, -1) })}
                  >
                    {t('hotelEditorial.up')}
                  </Button>
                  <Button
                    disabled={locked || index === images.length - 1}
                    onClick={() => change({ ...draft, images: moveImage(images, index, 1) })}
                  >
                    {t('hotelEditorial.down')}
                  </Button>
                  <Button
                    disabled={locked}
                    onClick={() => change({ ...draft, images: images.filter((_, i) => i !== index) })}
                  >
                    {t('hotelEditorial.remove')}
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
          {imageCapability.data?.enabled ? (
            <Button component="label" disabled={locked || upload.isLoading || images.length >= 50}>
              {t('hotelEditorial.upload')}
              <input
                type="file"
                hidden
                accept="image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload.mutate(file);
                  e.target.value = '';
                }}
              />
            </Button>
          ) : (
            <Alert severity="info">{t('hotelEditorial.uploadUnavailable')}</Alert>
          )}
          {upload.isError && <Alert severity="error">{t('hotelEditorial.uploadError')}</Alert>}
          <Button disabled={locked} onClick={() => change(restoreField(draft, 'images'))}>
            {t('hotelEditorial.restoreImages')}
          </Button>
          <TextField
            select
            label={t('hotelEditorial.previewLanguage')}
            value={language}
            disabled={locked}
            onChange={(e) => {
              setLanguage(e.target.value);
              setPreviewed(null);
            }}
          >
            {['es', 'en', 'pt'].map((lang) => (
              <MenuItem key={lang} value={lang}>
                {lang}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('hotelEditorial.reason')}
            value={reason}
            disabled={locked}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 512 }}
          />
          {conflict && (
            <Alert severity="error">
              {t('hotelEditorial.conflict')}{' '}
              <Button
                onClick={() => {
                  setDraft(null);
                  setPreviewed(null);
                  setConflict(false);
                  publication.refetch();
                  history.refetch();
                }}
              >
                {t('hotelEditorial.reload')}
              </Button>
            </Alert>
          )}
          {!conflict && (preview.isError || save.isError) && (
            <Alert severity="error">{t('hotelEditorial.saveError')}</Alert>
          )}
          <Stack direction="row" spacing={2}>
            <Button
              disabled={locked || preview.isLoading || upload.isLoading}
              onClick={() => preview.mutate({ key: currentKey, overrides: draft, version, language })}
            >
              {t('hotelEditorial.preview')}
            </Button>
            <Button
              disabled={
                locked || !reason.trim() || previewed?.key !== currentKey || upload.isLoading || preview.isLoading
              }
              onClick={() => save.mutate()}
            >
              {t('hotelEditorial.publish')}
            </Button>
            <Button disabled={save.isLoading || upload.isLoading} onClick={() => setDraft(null)}>
              {t('hotelEditorial.cancel')}
            </Button>
          </Stack>
          {previewed?.key === currentKey && (
            <Box>
              <Typography variant="h6">{t('hotelEditorial.preview')}</Typography>
              <Typography>{previewed.data.content.display_name}</Typography>
              {previewed.data.content.descriptions?.map((d, i) => (
                <Typography key={i} sx={{ whiteSpace: 'pre-wrap' }}>
                  {d.language} · {d.kind}: {d.text}
                </Typography>
              ))}
              <Stack direction="row" gap={1} flexWrap="wrap">
                {previewed.data.content.images?.map(
                  (i, n) =>
                    safeHotelImageUrl(i.url) && (
                      <img
                        key={n}
                        src={i.url}
                        alt={t('hotelEditorial.image', { index: n + 1 })}
                        width={120}
                        height={80}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ),
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
      <Typography variant="h6" sx={{ mt: 3 }}>
        {t('hotelEditorial.history')}
      </Typography>
      {history.isError && <Alert severity="error">{t('hotelEditorial.historyError')}</Alert>}
      {history.data?.data.map((event) => (
        <Box key={event.event_id} sx={{ my: 1 }}>
          <Typography>
            {event.created_at} · {t('hotelEditorial.version', { version: event.version })} · {event.operation}
          </Typography>
          <Typography>
            {event.actor_id}: {event.reason}
          </Typography>
        </Box>
      ))}
      <Button disabled={offset === 0 || history.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 10))}>
        {t('hotelEditorial.previous')}
      </Button>
      <Button
        disabled={!history.data?.meta.has_more || history.isFetching || offset >= 10000}
        onClick={() => setOffset((v) => v + 10)}
      >
        {t('hotelEditorial.next')}
      </Button>
    </Box>
  );
}
